/**
 * AddMentorModal Component
 *
 * Modal for admin to directly add a mentor via PAPI user search.
 * Skips the request/approval flow.
 */

import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import FormField from '@cloudscape-design/components/form-field';
import Link from '@cloudscape-design/components/link';
import { UserSearchSelect } from './UserSearchSelect';
import { UserSearchResult } from '../api/users';
import { useAddMentor } from '../hooks/queries/useMentors';
import { badgePhotoUrl, phoneToolUrl } from '../lib/externalUrls';

export interface AddMentorModalProps {
  visible: boolean;
  cohortId: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

export function AddMentorModal({
  visible,
  cohortId,
  onDismiss,
  onSuccess,
}: AddMentorModalProps) {
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchResetKey, setSearchResetKey] = useState(0);

  const addMentor = useAddMentor();

  const resetState = () => {
    setSelectedUser(null);
    setError(null);
    setSearchResetKey(k => k + 1);
  };

  const handleUserSelect = (user: UserSearchResult) => {
    setSelectedUser(user);
    setError(null);
  };

  const handleDismiss = () => {
    if (!addMentor.isPending) {
      resetState();
      onDismiss();
    }
  };

  const handleConfirm = () => {
    if (!selectedUser) return;

    setError(null);
    addMentor.mutate(
      { cohortId, userId: selectedUser.login },
      {
        onSuccess: () => {
          resetState();
          onSuccess();
          onDismiss();
        },
        onError: (err: any) => {
          if (
            err.response?.statusCode === 409 ||
            err.message?.includes('already a mentor')
          ) {
            setError('This user is already a mentor for this cohort');
          } else {
            setError(err.message || 'Failed to add mentor');
          }
        },
      },
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Add mentor"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={addMentor.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={addMentor.isPending}
              disabled={!selectedUser || addMentor.isPending}
            >
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormField
          label="Search for user"
          description="Search by name or alias to add as a mentor"
        >
          <UserSearchSelect
            key={searchResetKey}
            onSelect={handleUserSelect}
            placeholder="Search by name or alias..."
            disabled={addMentor.isPending}
            clearOnSelect={false}
          />
        </FormField>

        {selectedUser && (
          <SpaceBetween size="xs">
            <Box fontWeight="bold">Selected:</Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: '#f2f3f3', borderRadius: '8px' }}>
              <img src={badgePhotoUrl(selectedUser.login)} alt={selectedUser.name} style={{ width: 40, height: 40, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Box fontWeight="bold">{selectedUser.name}</Box>
                <Box color="text-body-secondary" fontSize="body-s"><Link href={phoneToolUrl(selectedUser.login)} external fontSize="body-s">@{selectedUser.login}</Link> — {selectedUser.title}</Box>
              </div>
        <Button variant="icon" iconName="close" ariaLabel="Clear selection" onClick={resetState} />
            </div>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
